/**
 * Canonical notification destination resolver for React.
 *
 * Resolves target URL following the 4-tier resolution order:
 * 1. event_type
 * 2. explicit destination (if present)
 * 3. legacy notif_type
 * 4. fallback (/dashboard)
 */
// Roles that see the Purchase Orders tab in Approvals (mirrors
// PO_APPROVER_ROLES in pages/Approvals.jsx). Anyone else who gets a PO
// notification (e.g. the PO's creator) goes to the PO itself.
const PO_APPROVER_ROLES = ["super_admin", "admin", "finance", "finance_officer"];

function currentRole() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return localStorage.getItem("role") || user.role || "";
  } catch {
    return "";
  }
}

// A fully-completed PO is waiting to be approved & closed — approvers land
// straight on that tab; everyone else opens the PO.
function poCompletedDestination(entity_id) {
  if (PO_APPROVER_ROLES.includes(currentRole())) return "/approvals?tab=purchase_orders";
  return entity_id ? `/purchase-orders/${entity_id}` : "/purchase-orders";
}

export function getNotificationDestination(notif) {
  if (!notif) return "/dashboard";
  const { event_type, notif_type, entity_id, destination } = notif;

  // Service notifications carry the call ID (older item events carry the parent call ID).
  if (event_type?.startsWith("service_call_") || event_type?.startsWith("service_item_")) {
    return entity_id ? `/service-calls?call=${encodeURIComponent(entity_id)}` : "/service-calls";
  }

  // 1. event_type resolution
  if (event_type) {
    switch (event_type) {
      case "task_pending_approval":
        return entity_id ? `/approvals?type=task&task=${entity_id}` : "/approvals?type=task";

      case "task_assigned":
      case "task_acknowledged":
      case "task_overdue":
      case "task_approved":
      case "task_rejected":
        return entity_id ? `/tasks?task=${entity_id}` : "/tasks";

      case "expense_submitted":
      case "expense_pending_finance":
      case "expense_pending_admin":
      case "expense_approved":
      case "expense_rejected":
        return entity_id ? `/expenses?expense=${entity_id}` : "/expenses";

      case "leave_submitted":
        return entity_id ? `/approvals?type=leave&leave=${entity_id}` : "/approvals?type=leave";

      // No entity-specific deep link once a request leaves the pending
      // queue — the admin's Pending Approvals list won't contain it anymore
      // (cancelled), so this just lands on the Leave tab generally.
      case "leave_cancelled":
        return "/approvals?type=leave";

      case "leave_approved":
      case "leave_rejected":
        return entity_id ? `/leave?leave=${entity_id}` : "/leave";

      case "po_completed":
        return poCompletedDestination(entity_id);

      case "po_item_completed":
      case "po_progress_updated":
      case "po_status_update":
      case "po_closed":
        return entity_id ? `/purchase-orders/${entity_id}` : "/purchase-orders";

      default:
        break;
    }
  }

  // 2. Explicit destination if present
  if (destination) {
    if (destination === "approvals") {
      return entity_id ? `/approvals?type=task&task=${entity_id}` : "/approvals";
    }
    if (destination === "tasks") {
      return entity_id ? `/tasks?task=${entity_id}` : "/tasks";
    }
    if (destination === "expenses") {
      return entity_id ? `/expenses?expense=${entity_id}` : "/expenses";
    }
  }

  // 3. Legacy notif_type fallback
  switch (notif_type) {
    case "task":
      return entity_id ? `/tasks?task=${entity_id}` : "/tasks";
    case "expense":
      return entity_id ? `/expenses?expense=${entity_id}` : "/expenses";
    case "service_call":
      return entity_id ? `/service-calls?call=${entity_id}` : "/service-calls";
    case "crm":
      return entity_id ? `/crm?crm=${entity_id}` : "/crm";
    case "leave":
      return entity_id ? `/leave?leave=${entity_id}` : "/leave";
    case "project_po_completed":
      return poCompletedDestination(entity_id);
    case "project_po_assigned":
    case "project_po_overdue":
    case "project_po_item_completed":
    case "project_po_progress":
    case "project_po_status_update":
    case "project_po_closed":
      return entity_id ? `/purchase-orders/${entity_id}` : "/purchase-orders";
    default:
      return "/dashboard";
  }
}
